import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useLayoutEffect,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import {
  addCart,
  removeCart,
  storeSeasonality,
  isDownloading,
  clearCart,
} from "../store/reducer";
import {
  downloadItem,
  toSnakeCase,
  downloadFile,
  confirmDelete,
  getDocsByCategoryID,
  getHotelNameByID,
  deepObjectComparison,
  deepComparison,
  isObjectInList,
  filterDocuments,
} from "./utilities";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ConfirmPopup } from "primereact/confirmpopup";
import { LangContext } from "../context/context";
import logo from "../assets/img/logo-bch.png";
import { DocSkeleton } from "./skeleton";
import { languages, existInCart, toAddFilter } from "./constant";
import * as formik from "formik";
import { ErrorMessage } from "formik";
import * as yup from "yup";
import loader from "../assets/Icons/anim-36-frames-03.gif";
import { ProgressBar } from "primereact/progressbar";
import { basename } from "../config";
import { saveCart, deleteCart as removeItemToDB, getCart } from "../api/api";

export const Heading = ({ heading, Desc }) => {
  /**
   * The `Heading` component  renders a heading with a description in a div element.
   * @returns The `Heading` component is being returned, which consists of a `div` element with the class
   * names "main-content-header welcome-msg text-center", an `h3` element displaying the `heading` prop
   * value, and a `p` element containing the `Desc` prop value.
   */

  const isDownloading = useSelector((state) => state.cart.isDownloading);

  return (
    <>
      <div className="sticky-top main-content-header welcome-msg">
        <div className="">
          <h3 className="text-capitalize">{heading}</h3>
          <p className="">
            <Desc />
          </p>
        </div>
      </div>
      {isDownloading ? (
        <ProgressBar
          mode="indeterminate"
          style={{ height: "1px" }}
          color={"#b4a064"}
          className="progress-bar"
        ></ProgressBar>
      ) : null}
    </>
  );
};

export const MainPageHead = (props) => {
  /**
   * The MainPageHead component renders a header section for a website with a logo, welcome message,
   * title, and website description.
   * @param props - In the code snippet you provided, the `MainPageHead` component is a functional
   * component that takes `props` as a parameter. However, in the code snippet you shared, the `props`
   * parameter is not being used within the component.
   * @returns The MainPageHead component is being returned, which includes a logo, a welcome message, the
   * app title "BEACHCOMBER B2B", and a website description.
   */

  const { t } = useTranslation();
  return (
    <>
      <div className="mb-5 text-center">
        <a href={"/"}>
          <img alt="beachcomber logo" src={logo} className="logo" />
        </a>
        <p className="mb-1">{t("welcome_to_the_website")}</p>
        <h3 className="app-title">
          BEACHCOMBER<span className="bcb-primary-color">B2B</span>
        </h3>
        <p className="text-capitalize">{t("website")}</p>
      </div>
    </>
  );
};

export const SheetsTable = ({ sheet }) => {
  /**
   * Renders a table component displaying sheets data.
   *
   * @param {Object} props - The props object containing the sheet data.
   * @param {Object} props.sheet - The sheet object containing the data to be displayed.
   * @param {Array} props.sheet.columns - The array of column objects defining the table columns.
   * @param {string} props.sheet.name - The name of the sheet.
   * @param {string} props.sheet.category - The category of the sheet.
   * @param {string} props.sheet.date - The date of the sheet.
   * @param {Array} props.sheet.data - The array of data rows to be displayed in the table.
   * @param {string} props.sheet.title - The title of the sheet.
   * @return {JSX.Element} The rendered table component.
   */
  const { t } = useTranslation();
  const addedItemIncart = useSelector((state) => state.cart);
  const userID = useSelector((state) => state.user.userID);
  const cart = addedItemIncart.cart;
  const dispatch = useDispatch();
  const [isExisting, setIsExisting] = useState(false);

  let columns = [
    {
      field: "language",
      header: t("language"),
    },
    {
      field: "type",
      header: t("file_type"),
    },
    {
      field: "resolution",
      header: t("resolution"),
    },
    {
      field: "size",
      header: t("file_size"),
    },
    {
      field: "update",
      header: t("uploaded_on"),
    },
    {
      field: "download",
      header: t("download"),
    },
  ];

  const addItem = async (e, item) => {
    e.preventDefault();

    const isIncluded = isObjectInList(item, cart);

    let value = {};
    value["userID"] = userID;
    value["file"] = item;

    if (isIncluded) {
      setIsExisting(true);
      return;
    } else {
      let resp = await saveCart(value);
      if (resp) {
        let userCart = await getCart(userID);

        if (userCart) {
          userCart.forEach((item) => {
            const isIncluded = isObjectInList(item, cart);

            if (isIncluded) {
              return;
            }

            dispatch(addCart(item));
          });
        }
      }
    }
  };

  const renderTooltip = (val) => {
    function toSnakeCase(str) {
      return str.replace(/\s+/g, "_").replace(/&/g, "and").toLowerCase();
    }

    let name = toSnakeCase(val);

    return (
      <Tooltip id={"button-tooltip-" + name} className="custom-tooltip ">
        {t(name)}
      </Tooltip>
    );
  };

  const downloadColumnTemplate = (item) => {
    const isIncluded = isObjectInList(item, cart);

    // cart.find((elt) => deepObjectComparison(elt, item));

    return (
      <div className="row g-2 table-download-column">
        <div className="col-12 col-sm-6 col-md-6 col-lg-6 ">
          <OverlayTrigger
            placement={"bottom"}
            delay={{ show: 50, hide: 50 }}
            overlay={renderTooltip("Download")}
          >
            <a href="/" className="" onClick={(e) => downloadItem(e, item)}>
              <i className="bi bi-download "></i>
            </a>
          </OverlayTrigger>
        </div>
        <div className="col-12 col-sm-6 col-md-6 col-lg-6">
          <OverlayTrigger
            placement={"bottom"}
            delay={{ show: 50, hide: 50 }}
            overlay={renderTooltip(
              isExisting || isIncluded ? existInCart : "Add to Cart"
            )}
          >
            <a
              href="/"
              className={isIncluded ? "disabled-link" : " "}
              aria-disabled={isIncluded ? true : false}
              onClick={(e) => {
                if (e.currentTarget.getAttribute("aria-disabled") === "true") {
                  e.preventDefault();
                  return;
                }
                addItem(e, item);
              }}
            >
              <i className="bi bi-cart"></i>
            </a>
          </OverlayTrigger>
        </div>
      </div>
    );
  };

  if (sheet?.columns?.length > 0) {
    columns = sheet.columns;
  }

  let temp = [];

  if (sheet.name && sheet.name !== "") {
    temp.push(sheet.name);
  }
  if (sheet.category && sheet.category !== "") {
    temp.push(sheet.category);
  }
  if (sheet.date && sheet.date !== "") {
    temp.push(sheet.date);
  }

  return (
    <>
      <div className="table-responsive" key={sheet.date}>
        <DataTable
          key={"table-" + sheet.date}
          value={sheet.data}
          emptyMessage="No Document found."
          header={
            <div key={"table-" + sheet.date}>
              <div className=" ">
                <span className="">{sheet.title}</span>
              </div>
            </div>
          }
          className="factsheet-table"
        >
          {columns.map((item, index) => {
            if (index === columns.length - 1) {
              return (
                <Column
                  key={"columns-" + item.index}
                  field={item.field}
                  header={item.header}
                  body={(data) => downloadColumnTemplate(data)}
                ></Column>
              );
            } else {
              return (
                <Column
                  key={"columns-" + item.index}
                  field={item.field}
                  header={item.header}
                  className="text-uppercase"
                ></Column>
              );
            }
          })}
        </DataTable>
      </div>
    </>
  );
};

export const SheetTemplate = (props) => {
  /**
   * A template for rendering a list of sheets with their respective data.
   *
   * @param {object} props - The properties passed to the component.
   * @param {array} props.data - The list of sheet data to be rendered.
   * @param {string} props.name - The name to be assigned to each sheet.
   * @return {JSX.Element} The rendered sheet template.
   */
  if (!props.data || props.data.length === 0) {
    return <>no data found</>;
  }

  return (
    <>
      {props.data.map((item, index) => {
        item.name = props.name;

        return (
          <div key={index}>
            <hr />
            <Row className="gx-5">
              <Col sm="2" className="d-flex align-items-center ">
                <div>
                  <img
                    src={item.src}
                    alt="Factsheet"
                    className="factsheet-img "
                  />
                </div>
              </Col>
              <Col sm="10">
                <SheetsTable sheet={item} />
              </Col>
            </Row>
          </div>
        );
      })}
    </>
  );
};

export const NoDataFound = () => (
  /**
   * A component that renders a message when no data is found.
   * @returns {JSX.Element} A JSX element displaying a message when no data is found.
   */

  <div className="d-flex justify-content-center lead text-center">
    <div className="row">
      <div>
        <T value="No data found" />
        <span className={"empty_folder"}></span>
      </div>
    </div>
  </div>
);

export const SheetView = (props) => {
  /**
   * The `SheetView` component in JavaScript fetches and displays documents based on category, hotel ID,
   * and season, with the ability to filter and display a disclaimer for logo usage.
   * @param props - Props are the properties that are passed to a React component. In this case, the
   * `SheetView` component is receiving props as an argument. These props can be any data or functions
   * that are passed down from a parent component to the `SheetView` component. The `props` object
   * contains all
   * @returns The `SheetView` component is being returned, which contains JSX elements for rendering a
   * sheet view interface. The component includes various state variables, useEffect hooks for data
   * fetching and updates, query handling using `useQuery` hook, event handlers, and conditional
   * rendering based on data availability. The component also renders a disclaimer section and other UI
   * components based on certain conditions.
   */

  const location = useLocation();
  const { id } = useParams();
  const app = useSelector((state) => state.user);
  const menu = useSelector((state) => state.menu);
  const { lang } = useContext(LangContext);
  const [hotelName, setHotelName] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const [categoryID, setCategoryID] = useState(null);
  const [pageTitle, setPageTitle] = useState(<T value={categoryName} />);
  const [subtitle, setSubtitle] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const queryClient = useQueryClient();
  const categories = app.docCategories;
  const [season, setSeason] = useState(null);
  const [hotelID, setHotelID] = useState(id);
  const [language, setLanguage] = useState(lang);

  const [docs, setDoscs] = useState([]);

  useEffect(() => {
    if (props.category) {
      setCategoryID(props.category.id);
    } else {
      setCategoryID(menu.activeItem.id);
    }

    setHotelID(id);
    setSeason(0);
    setLanguage("en");
  }, [location]);

  useEffect(
    () => {
      let name;

      if (props.category) {
        name = props.category.name;
        setCategoryID(props.category.id);
      } else if (menu.activeItem) {
        name = menu.activeItem.name;

        setCategoryID(menu.activeItem.id);
      }

      setCategoryName(name || categoryName);

      if (name) {
        setPageTitle(<T value={name.replace("/", "_")} />);
      }
    },
    [location, props.category, menu.activeItem, categoryName],
    categoryID
  );

  const queryKey = ["docs", lang, categoryName, hotelID, season];

  const { data: datas, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      getDocsByCategoryID(
        language,
        categoryName,
        categoryID,
        categories,
        hotelID,
        season
      ),
    enabled:
      !!language &&
      !!categoryName &&
      (hotelID === undefined || hotelID !== undefined),
    cacheTime: 1000 * 60 * 30,
    staleTime: 1000 * 60 * 30,
  });

  useEffect(() => {
    if (id) {
      setHotelID(id);
    } else {
      setHotelID(null);
    }
  }, [id]);

  useEffect(() => {
    setHotelName(null);
    if (id) {
      const hotelName = getHotelNameByID(id, app.hotels);
      setHotelName(hotelName);
    }
  }, [id, app.hotels]);

  const handleFilter = async (e, criterias) => {
    e.preventDefault();
    setHotelID(criterias.hotel);
    setSeason(criterias.season);
    setLanguage("en");
  };

  const Disclaimer = () => {
    const [isclose, setIsclose] = useState(false);
    return (
      <div className={isclose ? "h" : " disclaimer "}>
        <button
          className="close-btn"
          aria-label="Close"
          onClick={(e) => {
            setIsclose(true);
          }}
        >
          &times;
        </button>
        <h6>Disclaimer: Logo Usage</h6>
        <p>
          When downloading the Beachcomber logos and all associated hotel logos,
          please ensure that you adhere to the graphic charts and guidelines
          provided. Proper use of these logos is essential to maintain brand
          consistency and integrity. For detailed instructions on how to
          correctly use the logos, please refer to our graphic charts and
          guidelines. Failure to comply with these guidelines may result in
          unauthorized use of the logos.
        </p>
      </div>
    );
  };

  const dataToDisplay = datas && datas.length > 0 && datas[0].data.length > 0;

  return (
    <div>
      <Heading heading={pageTitle} Desc={() => subtitle} />

      <div className="main-content">
        {categoryName && categoryName.toLowerCase() === "logos" && (
          <Disclaimer />
        )}

        {categoryID && toAddFilter.includes(Number(categoryID)) && !id && (
          <DocFilter callback={handleFilter} category={categoryName} />
        )}

        {isLoading ? (
          <DocSkeleton />
        ) : datas && dataToDisplay ? (
          datas.map((sheet, index) => (
            <div className="mb-5" key={index}>
              <div className="px-3">
                <h4>{sheet.type}</h4>
              </div>
              <SheetTemplate data={sheet.data} name={hotelName} />
            </div>
          ))
        ) : (
          <NoDataFound />
        )}
      </div>
    </div>
  );
};

export const CartItems = () => {
  /**
   * A functional component that renders a list of cart items, allowing users to select, remove, and download items.
   *
   * @return {JSX.Element} The JSX element representing the cart items list.
   */

  const userID = useSelector((state) => state.user.userID);
  const { t } = useTranslation();
  const cart = useSelector((state) => state.cart);
  const dispatch = useDispatch();
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [selectedItems, setSelectedItems] = useState(
    new Array(cart.cart.length).fill(false)
  );

  const cartItems = cart.cart;

  const handleSelectAllItem = () => {
    const newSelectionState = !isAllSelected;
    setIsAllSelected(newSelectionState);
    setSelectedItems(new Array(cartItems.length).fill(newSelectionState));
  };

  const handleSelectItem = (index) => {
    const newSelectedItems = [...selectedItems];
    newSelectedItems[index] = !newSelectedItems[index];
    setSelectedItems(newSelectedItems);
    setIsAllSelected(newSelectedItems.every(Boolean));
  };

  const removeItem = async (e, item) => {
    e.preventDefault();
    let value = {};
    value["files"] = [];
    value["files"].push(Number(item.id));
    value["userID"] = Number(userID);
    dispatch(removeCart(item));
    await removeItemToDB(value);
  };

  const deleteCart = async (e) => {
    e.preventDefault();
    let value = {};
    value["files"] = [];
    value["userID"] = Number(userID);

    cartItems.forEach((item) => {
      value["files"].push(Number(item.id));
    });

    dispatch(clearCart());
    await removeItemToDB(value);
  };

  const renderTooltip = (val) => {
    function toSnakeCase(str) {
      return str.replace(/\s+/g, "_").replace(/&/g, "and").toLowerCase();
    }

    let name = toSnakeCase(val);

    return (
      <Tooltip
        id={"button-tooltip-" + name}
        className="custom-tooltip text-capitalize"
      >
        {t(name)}
      </Tooltip>
    );
  };

  const customMenu = (item) => {
    return (
      <>
        <div className="row g-2 cart-action-icon">
          <div className="col-12 col-sm-6 col-md-6 col-lg-6">
            <OverlayTrigger
              placement={"top"}
              delay={{ show: 50, hide: 50 }}
              overlay={renderTooltip("Remove")}
            >
              <a
                href="/"
                className=""
                key={"delete-" + item.name}
                onClick={(e) => removeItem(e, item)}
              >
                <i className="bi bi-trash"></i>
              </a>
            </OverlayTrigger>
          </div>
          <div className="col-12 col-sm-6 col-md-6 col-lg-6">
            <OverlayTrigger
              placement={"top"}
              delay={{ show: 50, hide: 50 }}
              overlay={renderTooltip("Download")}
            >
              <a
                href="/"
                className=""
                key={"download-" + item.name}
                onClick={(e) => downloadItem(e, item)}
              >
                <i className="bi bi-download"></i>
              </a>
            </OverlayTrigger>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Row>
        <div className="pl-5">
          {/* <div className="select_all">
            <input
              type="checkbox"
              id="cart_select_all"
              name="cart_select_all"
              checked={isAllSelected}
              className="me-3 select-checkbox"
              onChange={handleSelectAllItem}
            />
            <label htmlFor="cart_select_all" className="text-uppercase">
              {t("select_all_items")}
            </label>
          </div> */}
        </div>
        <div>
          {/* {cartMailTemplate(cartItems)} */}
          {cartItems.length > 0 ? (
            <ul className="custom-list">
              {cartItems.map((item, index) => (
                <li key={"element-" + index}>
                  <div className="row g-2 py-3">
                    <div className="col-12 col-sm-11">
                      <div className="row g-3">
                        <div className="col-12 col-sm-auto">
                          {/* <input
                            type="checkbox"
                            name={"cart_select_item" + item.id}
                            checked={selectedItems[index]}
                            className="select-checkbox"
                            onChange={() => handleSelectItem(index)}
                          /> */}
                        </div>
                        <div className="col-12 col-sm-2">
                          <div>
                            <img
                              src={item.src}
                              alt="Factsheet"
                              className="cart-img"
                            />
                          </div>
                        </div>
                        <div className="col-12 col-lg-9">
                          <div>
                            <h5>
                              {item.category} | {item.name} - {item.category} -{" "}
                              {item.date}
                            </h5>
                            <p>
                              {t("language")} :{" "}
                              <span className="text-uppercase">
                                {item.language}
                              </span>{" "}
                              | {t("file_type")} :{" "}
                              <span className="text-uppercase">
                                {item.type}
                              </span>
                            </p>
                            <div className="filesize">
                              {t("file_size")} : {item.size}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col col-sm-1 d-flex align-items-end">
                      {customMenu(item)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div>
              <p className="text-center p-4 fs-1 text-muted fw-light">
                {t("your_cart_is_empty")}
              </p>
            </div>
          )}
          <div className="row sticky-bottom">
            <div className="col d-flex justify-content-end mt-4">
              {cartItems.length > 0 ? (
                <button
                  type="button"
                  onClick={deleteCart}
                  className="btn bcb-btn bcb-btn-secondary text-uppercase px-4"
                >
                  <i className="bi bi-trash3 me-2"></i>
                  {t("clear_my_cart")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </Row>
    </>
  );
};

export const HotelFilter = (props) => {
  /**
   * Renders a filter component for hotels.
   *
   * @param {Object} props - The properties passed to the component.
   * @param {Function} props.callback - The callback function to be called when the filter is submitted.
   * @return {JSX.Element} The rendered filter component.
   */
  const { t } = useTranslation();
  const menu = useSelector((state) => state.menu);
  const hotels = useSelector((state) => state.user.hotels);
  let param = menu.parameter;
  let callback = props.callback;

  useEffect(() => {
    // callback({ urlParam: param });
  }, [param]);

  return (
    <>
      {!param ? (
        <Row className="filter g-2 ">
          <div>
            <Form className=" g-2 ">
              <Row>
                <Col sm={8}></Col>
                <Col sm={4} className="mt-4">
                  <Form.Group className="col-12 ">
                    <Form.Select
                      aria-label=""
                      name="hotel"
                      id="hotel"
                      placeholder="Select Hotel"
                    >
                      <option value="0" defaultValue>
                        Select Hotel
                      </option>
                      {hotels.map((hotel, index) => {
                        return (
                          <option value={hotel.hotel_id} key={index}>
                            {hotel.hotel_name}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </div>
        </Row>
      ) : null}
    </>
  );
};

export const DocFilter = (props) => {
  /**
   * Renders a filter component for documents.
   *
   * @param {Object} props - The properties passed to the component.
   * @param {Function} props.callback - The callback function to be called when the filter is submitted.
   * @return {JSX.Element} The rendered filter component.
   */

  const { t } = useTranslation();
  const location = useLocation();
  const menu = useSelector((state) => state.menu);
  const app = useSelector((state) => state.user);
  let temp_hotels = useSelector((state) => state.user.hotels);
  const formikRef = useRef(null);
  const [lang, setLang] = useState("en");
  const [season, setSeason] = useState(0);
  const [hotel, setHotel] = useState(0);

  const hotels =
    props.category?.toLowerCase() !== "logos"
      ? temp_hotels.slice(0, -1)
      : temp_hotels;

  let param = menu.parameter;
  let callback = props.callback;
  let seasons = app.seasons;

  useEffect(() => {
    setHotel(0);
    setSeason(0);
    setLang("en");
  }, [location]);

  const { Formik } = formik;

  const schema = yup.object().shape({
    hotel: yup
      .number()
      .required(<T value={"Hotel is required"} />)
      .notOneOf(["0"], <T value={"Hotel is required"} />),
    season: yup
      .number()
      .required(<T value={"Season is required"} />)
      .notOneOf(["0"], <T value={"Season is required"} />),
    // lang: yup.string().required(<T value={"Language Name is required"} />),
  });

  const submitHandler = (e) => {
    e.preventDefault();
    let values = {
      hotel: hotel,
      season: season,
      lang: lang,
    };

    callback(e, values);
  };

  const clearFilter = (e) => {
    e.preventDefault();
    setLang("en");
    setSeason(0);
    setHotel(0);
  };

  return (
    <>
      {!param ? (
        <Row className="filter g-2 d-flex justify-content-center mb-4">
          <div>
            {/* <Formik
              validationSchema={schema}
              onSubmit={submitHandler}
              initialValues={{
                hotel: hotel,
                season: season,
                lang: lang,
              }}
              innerRef={formikRef}
            >
              {({
                handleSubmit,
                handleChange,

                values,
                errors,
                handleBlur,
                isSubmitting,
              }) => (
                
              )}
            </Formik> */}
            <Form className="">
              <Row className="g-2 d-flex justify-content-center">
                <div className="col col-auto filter-icon-block">
                  <div className="">
                    <h6>
                      <T value={"Filters"} /> :
                    </h6>
                    <i className="bi bi-sliders filter-icon"></i>
                  </div>
                </div>
                <Form.Group className="col-12 col-sm-4 col-md-4 col-xl-4">
                  <Form.Label className="text-capitalize">
                    <T value={"Hotels"} /> :
                  </Form.Label>
                  <Form.Select
                    aria-label=""
                    name="hotel"
                    id="hotel"
                    value={hotel}
                    placeholder="Select Hotel"
                    onChange={(e) => setHotel(e.currentTarget.value)}
                  >
                    <option value="0" defaultValue>
                      <T value={"Select Hotel"} />
                    </option>
                    {hotels.map((hotel, index) => {
                      return (
                        <option value={hotel.hotel_id} key={index}>
                          {hotel.hotel_name}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>
                {props.category.toLowerCase().trim() ===
                "floor plans" ? null : (
                  <>
                    <Form.Group className="col-12 col-sm-3 col-md-3 col-xl-3">
                      <Form.Label className="text-capitalize">
                        <T value={"Date"} /> :
                      </Form.Label>
                      <Form.Select
                        aria-label=""
                        name="season"
                        id="season"
                        value={season}
                        placeholder="Select season"
                        onChange={(e) => setSeason(e.currentTarget.value)}
                        // isInvalid={!!errors.season}
                      >
                        <option value="0" defaultValue>
                          <T value={"Select season"} />
                        </option>
                        {seasons.map((season, index) => {
                          return (
                            <option value={season.tid} key={index}>
                              {season.title}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="col-12 col-sm-3 col-md-3 col-xl-3">
                      <Form.Label className="text-capitalize">
                        <T value={"Language"} /> :
                      </Form.Label>
                      <Form.Select
                        aria-label=""
                        name="lang"
                        id="lang"
                        placeholder="Select Language"
                        value={lang}
                        onChange={(e) => setLang(e.currentTarget.value)}
                      >
                        <option value="0" defaultValue>
                          <T value={"Select Language"} />
                        </option>
                        {languages.map((lang, index) => {
                          return (
                            <option value={lang} key={index}>
                              {lang.toUpperCase()}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </Form.Group>
                  </>
                )}

                <div className="col-12 col-sm-1 col-md-1 col-lg-1">
                  <div className="g-2 row pt-1">
                    <div className="col-12 col-sm-6 col-lg-6">
                      <Button
                        variant="dark"
                        className="d-flex justify-content-center bcb-btn-primary col-12"
                        onClick={submitHandler}
                      >
                        <i className="bi bi-caret-right-fill"></i>
                      </Button>
                    </div>
                    <div className="col-12 col-sm-6 col-lg-6">
                      <Button
                        variant="dark"
                        className=" bcb-btn-secondary  col-12 d-flex justify-content-center"
                        onClick={clearFilter}
                      >
                        clear
                      </Button>
                    </div>
                  </div>
                </div>
              </Row>
            </Form>
          </div>
        </Row>
      ) : null}
    </>
  );
};

export const T = (props) => {
  /**
   * Returns a translated string using the `t` function from the `useTranslation` hook.
   *
   * @param {Object} props - The props object containing the value to be translated.
   * @param {string} props.value - The value to be translated.
   * @return {JSX.Element} The translated string.
   */
  const { t } = useTranslation();

  return <>{t(toSnakeCase(props.value))}</>;
};

export const renderTooltip = (val, flag = false) => {
  /**
   * Returns a tooltip component with a translated string.
   *
   * @param {string} val - The value to be translated and displayed in the tooltip.
   * @return {JSX.Element} The rendered tooltip component.
   */
  function toSnakeCase(str) {
    return str.replace(/\s+/g, "_").replace(/&/g, "and").toLowerCase();
  }

  let name = toSnakeCase(val);

  return (
    <Tooltip id={"button-tooltip-" + name} className="custom-tooltip">
      <T value={name} />
    </Tooltip>
  );
};

export function RenderTableHeader(props) {
  /**
   * Renders a table header component with a clear filter button and a global search input field.
   *
   * @param {Object} props - The props object containing the clear filter and global search functionality.
   * @param {function} props.clearFilterDatatable - Function to clear the filter datatable.
   * @param {string} props.globalFilterValue - The current value of the global filter input field.
   * @param {function} props.onGlobalFilterChange - Function to handle changes to the global filter input field.
   * @return {JSX.Element} The rendered table header component.
   */
  return (
    <div className="d-flex justify-content-between">
      <div className="">
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          className="p-button-outlined bcb-btn-smaller "
          onClick={props.clearFilterDatatable}
        />
      </div>
      <div className=" p-input-icon-left ">
        <i className="pi pi-search pr-3" />
        <InputText
          value={props.globalFilterValue}
          onChange={props.onGlobalFilterChange}
          placeholder="Keyword Search"
          className="search-input"
        />
      </div>
    </div>
  );
}

export function ActionBtnTemplate(props) {
  /**
   * The `ActionBtnTemplate` function generates a set of buttons based on the provided `action` object
   * and handles the corresponding click events.
   * @param props - The `props` parameter is an object that contains the properties passed to the
   * `ActionBtnTemplate` component. These properties can include `actionBtn`, `itemID`, `handleView`,
   * `handleEdit`, `handlePrint`, `beforeDelete`, and `handleDelete`.
   * @returns a JSX element that contains an array of buttons based on the props passed to it. The
   * buttons are conditionally rendered based on the values of the `action` object. Each button has an
   * `onClick` event handler that calls a corresponding function passed as a prop. The JSX element also
   * includes a `<ConfirmPopup />` component.
   */

  let action = props.actionBtn ? props.actionBtn : null;
  let itemID = props.itemID;
  let itemName = props.name;
  let size = props.size ? "mt-" + props.size : " ";
  let data = props.data;
  let target = props.editTarget ? props.editTarget : null;
  let btn = [];

  if (action !== "undefined" && action !== null) {
    if (Object.keys(action).length !== 0) {
      for (const item in action) {
        if (
          item === "request" &&
          action[item] &&
          data["user_active"] === "nt"
        ) {
          btn.push(
            <Button
              id={"btn-edit-" + itemID}
              key={"btn-edit-" + itemID}
              icon="bi bi-pencil-square"
              className={"b2b-button-rounded  p-button-text "}
              onClick={(e) => {
                if (props.handleEdit) {
                  props.handleEdit(e, itemID);
                }
                e.preventDefault();
              }}
            />
          );
        }
        if (item === "edit" && action[item]) {
          btn.push(
            <Button
              id={"btn-edit-" + itemID}
              key={"btn-edit-" + itemID}
              icon="bi bi-pencil-square"
              className={"b2b-button-rounded  p-button-text "}
              onClick={(e) => {
                if (props.handleEdit) {
                  props.handleEdit(e, itemID);
                }
                e.preventDefault();
              }}
            />
          );
        }

        if (item === "delete" && action[item]) {
          btn.push(
            <Button
              id={"btn-delete-" + itemID}
              key={"btn-delete-" + itemID}
              icon="pi pi-trash"
              className={"b2b-button-rounded  p-button-text "}
              onClick={(e) => {
                if (props.beforeDelete) {
                  props.beforeDelete(itemID);
                  confirmDelete(e, props.handleDelete, itemName);
                }
                e.preventDefault();
              }}
            />
          );
        }
      }
    }
  }

  return (
    <>
      {btn.map((item, index) => (
        <span key={index}>{item}</span>
      ))}
      <ConfirmPopup />
      {props.toast}
    </>
  );
}

export const DownloadMenu = ({ item }) => {
  /**
   * A functional component that renders a download menu for the given item.
   * The component renders a dropdown menu with two options: "Download" and "Add to Cart".
   * The component also handles the logic for adding the item to the cart and
   * displaying a message if the item is already in the cart.
   * @param {Object} props - The props object containing the item data.
   * @param {Object} props.item - The item object containing the data to be displayed.
   * @return {JSX.Element} The JSX element representing the download menu component.
   */

  const addedItemIncart = useSelector((state) => state.cart);
  const cart = addedItemIncart.cart;
  const dispatch = useDispatch();
  const [isExisting, setIsExisting] = useState(false);

  const addItem = (e, item) => {
    e.preventDefault();

    const isIncluded = cart.find((elt) => deepComparison(elt, item));
    console.log("isIncluded:", isIncluded);
    if (isIncluded) {
      setIsExisting(true);
      return;
    }
    dispatch(addCart(item));
  };

  return (
    <div className="custom-dropdown-menu  row g-4 mt-0">
      <div className="col-12 col-sm-6 col-md-6 col-lg-6 ">
        <OverlayTrigger
          placement={"bottom"}
          delay={{ show: 50, hide: 50 }}
          overlay={renderTooltip("Download")}
        >
          <a href="/" className="" onClick={(e) => downloadItem(e, item)}>
            <i className="bi bi-download "></i>
          </a>
        </OverlayTrigger>
      </div>
      <div className="col-12 col-sm-6 col-md-6 col-lg-6">
        <OverlayTrigger
          placement={"bottom"}
          delay={{ show: 50, hide: 50 }}
          overlay={renderTooltip(
            isExisting
              ? "This document is already in your cart."
              : "Add to Cart"
          )}
        >
          <a
            href="/"
            className=""
            onClick={(e) => {
              addItem(e, item);
            }}
          >
            <i className="bi bi-cart"></i>
          </a>
        </OverlayTrigger>
      </div>
    </div>
  );
};

export const LoadingTemplate = () => {
  /**
   * A React component that renders a full-screen loading animation.
   *
   * @return {JSX.Element} The rendered loading template component.
   */

  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{ height: "100vh" }}
    >
      <div className="" role="status">
        <img src={loader} className="loader" alt="loader" />
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
};

export const ColorKeys = () => {
  /**
   * A functional component that renders a color key for the B2B platform.
   * The component renders a set of colored tags with corresponding text labels.
   * The component is used to display the status of rooms on the platform.
   * @return {JSX.Element} The JSX element representing the color key component.
   */

  return (
    <Row className="color-key-block">
      <Col sm={4}>
        <div className="my-3">
          <Tag severity="success" value="" className="color-key"></Tag>
          <span className="">
            <T value={"Rooms on a sell and report basis"} />
          </span>
        </div>
        <div className="">
          <Tag severity="" value="" className="color-key bcb-bg-blue"></Tag>
          <span className="">
            <T value={"Rooms above allocation available on request"} />
          </span>
        </div>
      </Col>
      <Col sm={4}>
        <div className="my-3">
          <Tag severity="" value="" className="color-key bcb-bg-yellow"></Tag>
          <span className="">
            <T value={"Rooms on request basis only"} />{" "}
          </span>
        </div>
        <div className="">
          <Tag severity="" value="" className="color-key bcb-bg-cyan"></Tag>
          <span className="">
            <T value={"Booking in allocation only (from 20 Dec to 08 Jan)"} />{" "}
          </span>
        </div>
      </Col>
      <Col sm={4}>
        <div className="my-3">
          <Tag severity="" value="" className="color-key bcb-bg-red"></Tag>
          <span className="">
            <T value={"Rooms not available"} />{" "}
          </span>
        </div>
        <div className="">
          <Tag severity="" value="" className="color-key bcb-bg-gray"></Tag>
          <span className="">
            {" "}
            <T value={"Rooms are not available due to renovations"} />{" "}
          </span>
        </div>
      </Col>
    </Row>
  );
};

export const DownloadTemplate = ({ item }) => {
  /**
   * DownloadTemplate is a React component that renders a single row in a table
   * with two columns: Download and Add to Cart. The component handles the logic
   * for downloading a file and adding the item to the cart. The component also
   * displays a message if the item is already in the cart.
   * @param {Object} props - The props object containing the item data.
   * @param {Object} props.item - The item object containing the data to be displayed.
   * @return {JSX.Element} The JSX element representing the download template component.
   */

  const [isExisting, setIsExisting] = useState(false);
  const addedItemIncart = useSelector((state) => state.cart);
  const cart = addedItemIncart.cart;
  const dispatch = useDispatch();

  const addItem = (e, item) => {
    e.preventDefault();

    const isIncluded = cart.find((elt) => deepComparison(elt, item));

    if (isIncluded) {
      setIsExisting(true);
      return;
    }
    dispatch(addCart(item));
  };

  const download = (e, item) => {
    const name = item.filename;
    const filePath = item.fileUrl;
    e.preventDefault();
    downloadFile(name, filePath);
  };

  return (
    <div className="row g-2 table-download-column">
      <div className="col-12 col-sm-6 col-md-6 col-lg-6 ">
        <OverlayTrigger
          placement={"bottom"}
          delay={{ show: 50, hide: 50 }}
          overlay={renderTooltip("Download")}
        >
          <a
            href="/"
            className="action-icon"
            onClick={(e) => download(e, item)}
          >
            <i className="bi bi-download "></i>
          </a>
        </OverlayTrigger>
      </div>
      {/* <div className="col-12 col-sm-6 col-md-6 col-lg-6">
        <OverlayTrigger
          placement={"bottom"}
          delay={{ show: 50, hide: 50 }}
          overlay={renderTooltip(isExisting ? existInCart : "Add to Cart")}
        >
          <a
            href="/"
            className="action-icon"
            onClick={(e) => {
              addItem(e, item);
            }}
          >
            <i className="bi bi-cart"></i>
          </a>
        </OverlayTrigger>
      </div> */}
    </div>
  );
};
